import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TriangleAlert, Loader2 } from "lucide-react";

const fmtDShort = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");

export const unclockedViolations = (err) => {
  const d = err?.response?.status === 409 ? err.response.data?.detail : null;
  return d && d.code === "unclocked_department" ? d.violations || [] : null;
};

export const UnclockedReasonDialog = ({ violations, onCancel, onConfirm, busy }) => {
  const [reasons, setReasons] = useState({});
  useEffect(() => { setReasons({}); }, [violations]);
  const ready = (violations || []).every((v) => (reasons[v.key] || "").trim().length >= 3);
  return (
    <Dialog open={!!violations} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-h-[88vh] max-w-xl overflow-y-auto" data-testid="unclocked-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><TriangleAlert className="h-5 w-5 text-amber-500" /> No clock-in for these departments</DialogTitle>
          <DialogDescription>
            These hours are saved under a department the employee did not clock into that day — this usually means the same hours are counted twice. Go back and fix them, or give a reason to save anyway (the reason is logged).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(violations || []).map((v) => (
            <div key={v.key} className="rounded-md border border-amber-400/60 bg-amber-50/60 p-3 dark:bg-amber-950/20" data-testid={`unclocked-viol-${v.key}`}>
              <div className="text-sm">
                <span className="font-semibold">{v.name}</span> · {fmtDShort(v.date)} — <b className="tabular-nums">{Number(v.hours).toFixed(2)}</b> hrs under{" "}
                <span className="font-semibold text-red-600 dark:text-red-400">{v.position}</span>
              </div>
              <div className="mb-2 text-xs text-muted-foreground">Clocked in as: {(v.clocked_positions || []).join(", ") || "—"}</div>
              <Textarea
                rows={2}
                placeholder="Reason (e.g. worked 2 hrs in Public Area, forgot to switch position)"
                value={reasons[v.key] || ""}
                onChange={(e) => setReasons((r) => ({ ...r, [v.key]: e.target.value }))}
                data-testid={`unclocked-reason-${v.key}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="unclocked-go-back-btn">Go back &amp; fix</Button>
          <Button onClick={() => onConfirm(reasons)} disabled={!ready || busy} data-testid="unclocked-save-anyway-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save anyway with reason
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
